import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SolveButton from "./components/Buttons/solveButton";
import SolveModal from "../SolveModal/SolveModal";
import LoadingModal from "../LoadingModal/LoadingModal";
import CongratulationsModal from "../CongratulationsModal/CongratulationsModal";
import Leaderboard from "./components/LeaderBoard/Leaderboard";
import AnswerModal from "./components/LeaderBoard/AnswerModal";

const QuestCard = ({ quest, isOwned = false }) => {
  const [creatorFullName, setCreatorFullName] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [gradedAnswer, setGradedAnswer] = useState(null);
  const [isGrading, setIsGrading] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [hoveredAnswer, setHoveredAnswer] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCreator = async () => {
      if (!quest?.creatorName) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/username/${quest.creatorName}`);
        const data = await res.json();
        if (res.ok) {
          const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
          setCreatorFullName(fullName);
        } else {
          setCreatorFullName(quest.creatorName);
        }
      } catch (err) {
        console.error("❌ Failed to fetch creator info:", err);
        setCreatorFullName(quest.creatorName);
      }
    };

    if (quest?.creatorName) fetchCreator();
  }, [quest]);

  const submitAnswer = async (answer) => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");
    const posterName = email?.split("@")[0];

    const payload = {
      questId: quest.questId,
      content: answer,
      posterName,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/answers/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        console.log("✅ Answer submitted:", data);
        const answerId = data.answerId;
        setIsGrading(true);
        checkGradingStatus(answerId);
      } else {
        console.error("❌ Failed to submit answer:", data.message);
        alert(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("❌ Server error:", err);
      alert("An error occurred while submitting your answer.");
    }
  };

  const checkGradingStatus = async (answerId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/answers/status/${answerId}`);
      const data = await res.json();

      if (res.ok && data?.points !== null && data?.feedback) {
        setGradedAnswer({
          points: data.points,
          feedback: data.feedback,
        });
        setIsGrading(false);
        setShowCongrats(true);
      } else {
        setTimeout(() => checkGradingStatus(answerId), 3000);
      }
    } catch (err) {
      console.error("❌ Failed to check grading status:", err);
      setTimeout(() => checkGradingStatus(answerId), 3000);
    }
  };

  const handleSolveClick = async () => {
    const email = localStorage.getItem("userEmail");
    const posterName = email?.split("@")[0];
    const token = localStorage.getItem("token");

    if (!posterName) {
      alert("User not logged in.");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/answers/check/${quest.questId}/${posterName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (res.ok) {
        if (data.alreadyAnswered) {
          alert("You've already answered this quest! 🚫 Try another one!");
        } else {
          setModalOpen(true);
        }
      } else {
        alert("Something went wrong. Try again later.");
      }
    } catch (err) {
      console.error("❌ Error checking answer existence:", err);
      alert("Server error. Try again later.");
    }
  };

  const handleCreatorClick = () => {
    if (!quest?.creatorName) return;
    navigate(`/${quest.creatorName}`);
  };

  if (!quest) return null;

  const { title, context, questImage } = quest;

  return (
    <>
      {/* ✅ Quest Card */}
      <div className="relative w-full max-w-[1317px] aspect-[16/5] bg-white rounded-[4vw] flex items-center px-4 md:px-6 lg:px-8 gap-4 
        transition-all duration-300 ease-in-out transform hover:scale-[1.015] 
        hover:shadow-[0_6px_30px_8px_rgba(73,195,251,0.25)] hover:bg-[#fefefe] group">

        {/* Left: Image */}
        <img
          src={questImage}
          alt="Quest"
          className="w-[20%] h-auto aspect-square rounded-[1.5vw] object-cover border border-indigo-700/10 
            shadow-[0_2px_8px_rgba(0,0,0,0.08)] group-hover:shadow-[0_4px_12px_rgba(73,195,251,0.2)] 
            transition-all duration-300 ease-in-out group-hover:scale-[1.04] group-hover:rotate-[2deg]"
        />

        {/* Right: Text */}
        <div className="flex flex-col justify-center gap-2 flex-grow">
          <div className="flex items-center gap-3">
            <div className="w-12 h-10 relative group-hover:scale-[1.05] transition-transform duration-300 ease-out">
              <div className="w-8 h-8 absolute left-[47.5px] top-[40px] origin-top-left rotate-180 bg-black rounded-full 
                transition-all duration-300 ease-out group-hover:translate-x-[1.5px] group-hover:-translate-y-[1.5px] 
                group-hover:scale-[1.05] group-hover:bg-[#49C3FB]" />
              <div className="w-3.5 h-3.5 absolute left-0 top-0 bg-black rounded-full 
                transition-all duration-300 ease-out group-hover:-translate-x-[1px] group-hover:translate-y-[1px] 
                group-hover:scale-[1.1] group-hover:bg-[#49C3FB]" />
            </div>

            <div className="text-black text-lg md:text-xl font-bold font-inter group-hover:text-[#49C3FB] transition duration-200">
              {title}
            </div>
          </div>

          <div className="pl-[52px] text-gray-700 text-sm md:text-base font-medium font-inter group-hover:text-gray-900 transition duration-200">
            {context}
          </div>

          <div className="pl-[52px] text-xs text-gray-500 font-medium font-['Inter'] mt-1">
            {isOwned ? (
              <span>
                created on{" "}
                <span className="text-black font-semibold">
                  {(() => {
                    const raw = quest.createdAt;
                    let date;
                    if (raw?._seconds) {
                      date = new Date(raw._seconds * 1000);
                    } else if (typeof raw === "string" && !isNaN(Date.parse(raw))) {
                      date = new Date(Date.parse(raw));
                    } else {
                      return "Unknown Date";
                    }
                    return date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                  })()}
                </span>
              </span>
            ) : (
              creatorFullName && (
                <span>
                  by{" "}
                  <button
                    onClick={handleCreatorClick}
                    className="text-cyan-600 hover:text-cyan-800 hover:underline hover:tracking-wide transition-all duration-200 ease-in-out"
                  >
                    {creatorFullName}
                  </button>
                </span>
              )
            )}
            {!isOwned && (
              <div className="mt-2">
                <SolveButton onClick={handleSolveClick} />
              </div>
            )}
          </div>
        </div>

        {/* Floating Leaderboard (only visible on hover) */}
<div className="absolute bottom-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
  <Leaderboard
    questId={quest.questId}
    setHoveredAnswer={setHoveredAnswer}
    setHoverPosition={setHoverPosition}
  />
</div>

      </div>

      {/* Floating Answer Preview */}
      <AnswerModal
        content={hoveredAnswer}
        visible={!!hoveredAnswer}
        position={hoverPosition}
      />

      {/* Modals */}
      <SolveModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={submitAnswer}
        questTitle={title}
        questContext={context}
      />

      <LoadingModal isOpen={isGrading} />

      <CongratulationsModal
        isOpen={showCongrats}
        onClose={() => setShowCongrats(false)}
        gradingData={gradedAnswer}
      />
    </>
  );
};

export default QuestCard;
